// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { Dataset } from '../data';
import type { NameRec, SeriesKey } from '../types';
import { SERIES_COLOUR, seriesLabel } from '../colours';
import { lineChart, type LineSeries } from '../charts';
import { escapeHtml, formatNumber } from '../utils/format';

export function nameKeyOf(s: string): string {
  return s.trim().replace(/\s+/g, ' ').toUpperCase();
}

/** Per-name drill-down drawer. Hash-linkable (#name=Noah). */
export function createDrilldown(data: Dataset): { open: (nameOrKey: string) => void; close: () => void } {
  const overlay = document.createElement('div');
  overlay.className = 'overlay';

  const drawer = document.createElement('div');
  drawer.className = 'drawer';
  drawer.setAttribute('role', 'dialog');
  drawer.setAttribute('aria-modal', 'true');
  drawer.innerHTML = '<div class="drawer-head"></div><div class="drawer-body"></div>';

  const closeBtn = document.createElement('button');
  closeBtn.className = 'modal-close';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.textContent = '✕';
  drawer.appendChild(closeBtn);

  document.body.append(overlay, drawer);

  let mode: 'rank' | 'count' = 'rank';

  const close = () => {
    overlay.classList.remove('open');
    drawer.classList.remove('open');
    if (location.hash.startsWith('#name=')) history.replaceState(null, '', location.pathname + location.search);
  };

  const resolve = (input: string): NameRec | undefined =>
    data.byKey.get(input) ?? data.byKey.get(nameKeyOf(input));

  const open = (input: string) => {
    const rec = resolve(input);
    if (!rec) return;
    mode = 'rank';
    render(rec);
    overlay.classList.add('open');
    drawer.classList.add('open');
    drawer.scrollTop = 0;
    closeBtn.focus();
    history.replaceState(null, '', `#name=${encodeURIComponent(rec.n)}`);
  };

  function seriesKeys(rec: NameRec): SeriesKey[] {
    return (Object.keys(rec.series) as SeriesKey[]).filter((k) => (rec.series[k] || []).length > 0);
  }

  function render(rec: NameRec): void {
    const head = drawer.querySelector('.drawer-head') as HTMLElement;
    const body = drawer.querySelector('.drawer-body') as HTMLElement;
    const keys = seriesKeys(rec);

    // Series-level totals (for the split view) and latest ranks.
    const totals = keys.map((k) => ({ k, total: (rec.series[k] || []).reduce((s, p) => s + p[2], 0) }));
    totals.sort((a, b) => b.total - a.total);
    const latest = data.meta.latestYear;

    head.innerHTML = `
      <div class="drawer-title">${escapeHtml(rec.n)}</div>
      <div class="drawer-sub">
        In the top 100 across ${rec.span[0]}–${rec.span[1]} · ${formatNumber(rec.totalCount)} babies all up
      </div>`;

    // peak info
    const peakYear = rec.bestYear;
    const parts: string[] = [];
    parts.push(`
      <div class="drawer-section">
        <div class="kv-grid">
          <div class="kv"><div class="kv-label">Best rank</div><div class="kv-value">#${rec.bestRank}</div><div class="stat-note">in ${peakYear}</div></div>
          <div class="kv"><div class="kv-label">Babies (total)</div><div class="kv-value">${formatNumber(rec.totalCount)}</div></div>
          <div class="kv"><div class="kv-label">First seen</div><div class="kv-value">${rec.span[0]}</div></div>
          <div class="kv"><div class="kv-label">Last seen</div><div class="kv-value">${rec.span[1]}</div></div>
        </div>
      </div>`);

    // trajectory chart with rank/count toggle
    parts.push(`
      <div class="drawer-section">
        <div style="display:flex;align-items:center;justify-content:space-between;gap:var(--space-sm);flex-wrap:wrap;margin-bottom:var(--space-sm)">
          <h3 style="margin:0">Trajectory</h3>
          <div class="seg" role="tablist" aria-label="Chart metric">
            <button class="seg-btn" data-mode="rank" aria-pressed="${mode === 'rank'}">Rank</button>
            <button class="seg-btn" data-mode="count" aria-pressed="${mode === 'count'}">Babies</button>
          </div>
        </div>
        <div class="chart-frame" data-role="traj"></div>
        <div class="legend" data-role="traj-legend"></div>
      </div>`);

    // split by state/sex
    const maxTotal = Math.max(...totals.map((t) => t.total), 1);
    parts.push(`
      <div class="drawer-section">
        <h3>Where the ${escapeHtml(rec.n)}s are</h3>
        ${totals
          .map(
            (t) => `
          <div class="compare-row">
            <div class="compare-label">${escapeHtml(seriesLabel(t.k))}</div>
            <div class="compare-track"><div class="compare-fill" style="width:${((t.total / maxTotal) * 100).toFixed(1)}%;background:${SERIES_COLOUR[t.k]}"></div></div>
            <div class="compare-val">${formatNumber(t.total)}</div>
          </div>`,
          )
          .join('')}
        <p class="stat-note" style="margin-top:var(--space-sm)">Total babies given this name in each state, by sex, across all years on record.</p>
      </div>`);

    body.innerHTML = parts.join('');
    paintTrajectory();

    body.querySelectorAll('[data-mode]').forEach((b) =>
      b.addEventListener('click', () => {
        mode = b.getAttribute('data-mode') as 'rank' | 'count';
        body.querySelectorAll('[data-mode]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
        paintTrajectory();
      }),
    );

    function paintTrajectory(): void {
      const slot = body.querySelector('[data-role="traj"]') as HTMLElement;
      const legend = body.querySelector('[data-role="traj-legend"]') as HTMLElement;
      if (!slot) return;
      slot.innerHTML = '';
      const series: LineSeries[] = keys.map((k) => ({
        id: k,
        label: k.replace('-', ' '),
        colour: SERIES_COLOUR[k],
        points: (rec.series[k] || []).map((p) => [p[0], mode === 'rank' ? p[1] : p[2]] as [number, number]),
      }));
      const allYears = series.flatMap((s) => s.points.map((p) => p[0]));
      const xDomain: [number, number] = [Math.min(...allYears), Math.max(latest, ...allYears)];
      let svg: SVGSVGElement;
      if (mode === 'rank') {
        svg = lineChart(series, {
          width: 460,
          height: 300,
          xDomain,
          yDomain: [1, 100],
          invertY: true,
          yTickVals: [1, 25, 50, 75, 100],
          yFmt: (v) => `#${Math.round(v)}`,
          valueNoun: '',
          endLabels: false,
        });
      } else {
        svg = lineChart(series, {
          width: 460,
          height: 300,
          xDomain,
          valueNoun: 'babies',
          endLabels: false,
        });
      }
      slot.appendChild(svg);
      legend.innerHTML = keys
        .map(
          (k) =>
            `<span class="legend-item"><span class="legend-swatch line" style="background:${SERIES_COLOUR[k]}"></span>${escapeHtml(seriesLabel(k))}</span>`,
        )
        .join('');
    }
  }

  overlay.addEventListener('click', close);
  closeBtn.addEventListener('click', close);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('open')) close();
  });

  return { open, close };
}
